import { CSSProperties, useState, useRef, useEffect } from "react";
import DebugBox from "components/shareable/DebugBox";
import ButtonSymphony from "elements/ButtonSymphony";
import CardSymphony from "elements/CardSymphony";
import InputSymphony from "elements/InputSymphony";
import PageSymphony from "elements/PageSymphony";
import useOrganizationV2 from "Hooks/useOrganizationV2";
import useUser from "Hooks/useUser";
import { CurrentBrand, OrganizationInvitationStatus, OrganizationStatus } from "modules/Types";
import TabsSymphony, { TabItem } from "elements/TabsSymphony";
import TabSymphony from "elements/TabSymphony";
import TableElement, { ColDef } from "elements/TableElement";
import useBrand from "Hooks/useBrand";
import CustomAvatar from "components/ProfileDropdown/CustomAvatar";
import Sty from "modules/Sty";
import { capitalizeFirstLetter, formatNumberToDisplayType, pluralize, slugToTitle, styx } from "modules/Utils";
import ModalSymphony from "elements/ModalSymphony";
import {
    CampaignStatus,
    CampaignType,
    CAMPAIGN_STATUS_INFO,
    OrganizationRole,
    PLAYBOOK_TYPE_INFO,
    PlaybookType,
    UserPermissionOnBrand,
    SYMPHONY_WEBSITE_URL,
} from "modules/Const";
import Icon from "elements/Icon";
import Colors from "modules/Colors";
import { useHistory, useLocation } from "react-router-dom";
import { Popover } from "@mui/material";
import AddBrandToOrganizationModal from "./OrganizationPage/componentsOrganization/AddBrandToOrganizationModal/AddBrandToOrganizationModal";
import BrandTier from "elements/BrandTier";
import SwitchSymphony, { SwitchType } from "elements/SwitchSymphony";
import { getOrganizationCampaigns_api, getOrganizationWebsites_api } from "api/organizationApi";
import TagSymphony from "elements/TagSymphony";
import dayjs from "dayjs";
import useWebsiteBuilder from "Hooks/useWebsiteBuilder";
import Loader from "elements/Loader";
import useCampaignBuilder from "Hooks/useCampaignBuilder";
import MembersPage from "./SettingsPage/Components/MembersPage";
import OrganizationTabBilling from "./OrganizationPage/tabViews/OrganizationTabBilling/OrganizationTabBilling";
import AdminId from "elements/AdminId";
import gravatar from "gravatar";

const PAGE_SIZE = 8;
const SEARCH_INPUT_MAX_WIDTH = 250

enum OrganizationTab {
    BRANDS = 'brands',
    TEAM = 'team',
    CAMPAIGNS = 'campaigns',
    WEBSITES = 'websites',
    FANS = 'fans',
    BILLING = 'billing',
}

enum TeamPlanCreateGoal {
    CAMPAIGN = 'campaign',
    WEBSITE = 'website',
}

const ORGANIZATION_TABS: TabItem[] = [
    {
        id: OrganizationTab.BRANDS,
        title: 'Brands',
    },
    {
        id: OrganizationTab.TEAM,
        title: 'Team',
    },
    {
        id: OrganizationTab.CAMPAIGNS,
        title: 'Campaigns',
    },
    {
        id: OrganizationTab.WEBSITES,
        title: 'Websites',
    },
    // {
    //     id: OrganizationTab.FANS,
    //     title: 'Fans',
    // },
    {
        id: OrganizationTab.BILLING,
        title: 'Billing',
    }
]

const OrganizationPageV2 = () => {
    const history = useHistory()
    const location = useLocation()
    const { currentUser, isSymphonyAdmin } = useUser();
    const {
        loadedBrandsDict,
        loadedBrands,
        currentBrand,
        setCurrentBrand,
        getBrand,
        getBrands,
    } = useBrand();
    const { setShowCreateNewCampaignModal } = useCampaignBuilder();
    const { setShowCreateNewWebsiteModalV2 } = useWebsiteBuilder();
    const {
        invitationStatus,
        organization,
        organizationBrandIds,
        organizationUsers,
        isLoadingOrganization,
        isOrganizationInitialized,

        hasOrganizationAdminAccess,

        getOrganization,
        createOrganization,
        createOrganizationBrand,
        updateOrganization,
        inviteOrganizationUsers,
        deleteOrganizationBrand,
        deleteOrganizationUser,
        updateOrganizationUser,
        updateUserBrand,
        respondOrganizationUserInvite,

        isLoadingOrganizationAction,
    } = useOrganizationV2();

    const {
        setup_status: organizationStatus,
    } = organization || {};

    const organizationId = organization?.id || 0;

    const [isCreateTeam, setIsCreateTeam] = useState<boolean>(false)

    const [selectedBrandIdsForOnboarding, setSelectedBrandIdsForOnboarding] = useState<number[]>([])

    const [organizationNameText, setOrganizationNameText] = useState<string>('')
    const [selectedOrganizationTab, setSelectedOrganizationTab] = useState<TabItem>(() => {
        const params = new URLSearchParams(location.search)
        const tabFromUrl = params.get('tab')
        return ORGANIZATION_TABS.find(tab => tab.id === tabFromUrl) || ORGANIZATION_TABS[0]
    })

    const [isAddTeamMemberModalOpen, setIsAddTeamMemberModalOpen] = useState<boolean>(false)
    const [isAddBrandModalOpen, setIsAddBrandModalOpen] = useState<boolean>(false)

    const [isEditTeamMemberModalOpen, setIsEditTeamMemberModalOpen] = useState<boolean>(false)
    const [isEditBrandModalOpen, setIsEditBrandModalOpen] = useState<boolean>(false)

    const [isRemoveBrandModalOpen, setIsRemoveBrandModalOpen] = useState<boolean>(false)
    const [isRemoveTeamMemberModalOpen, setIsRemoveTeamMemberModalOpen] = useState<boolean>(false)

    // Add the missing state variables for Popover
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const [activeBrandId, setActiveBrandId] = useState<string | number | null>(null)
    const [selectedBrand, setSelectedBrand] = useState<CurrentBrand | null>(null)
    const [organizationUsersTemp, setOrganizationUsersTemp] = useState<any | null>(null)

    const [selectedTeamMember, setSelectedTeamMember] = useState<any | null>(null)

    // Create a ref for each brand's contextual menu icon
    const menuIconRefs = useRef<Map<string | number, HTMLDivElement>>(new Map())

    const [brandSearchText, setBrandSearchText] = useState<string>('')
    const [teamMemberSearchText, setTeamMemberSearchText] = useState<string>('')

    const [firstNameText, setFirstNameText] = useState<string>('')
    const [lastNameText, setLastNameText] = useState<string>('')
    const [emailText, setEmailText] = useState<string>('')
    const [roleNewTeamMemberSelected, setRoleNewTeamMemberSelected] = useState<string>(OrganizationRole.MEMBER)

    const [marketingCampaigns, setMarketingCampaigns] = useState<any[]>([])
    const [totalCampaigns, setTotalCampaigns] = useState<number>(0)
    const [marketingCampaignsPage, setMarketingCampaignsPage] = useState<number>(0);

    const [websites, setWebsites] = useState<any[]>([])
    const [websitesPage, setWebsitesPage] = useState<number>(0);

    const [isLoadingCampaigns, setIsLoadingCampaigns] = useState<boolean>(false);
    const [isLoadingWebsites, setIsLoadingWebsites] = useState<boolean>(false);

    const [isBrandSelectorModalOpen, setIsBrandSelectorModalOpen] = useState<boolean>(false);
    const [createGoal, setCreateGoal] = useState<TeamPlanCreateGoal | null>(null);
    const [brandSelectorSearchText, setBrandSelectorSearchText] = useState<string>('');

    const getGravatarUrl = (email: string) => {
        return gravatar.url(email, { protocol: 'https', d: 'retro', s: '200' })
    }

    const brandRowData = organizationBrandIds?.map((brandId) => {
        if (!loadedBrandsDict?.[brandId]) {
            return null
        }

        return loadedBrandsDict?.[brandId]
    }).filter(brand => brand !== null) || []

    const brandColumns: ColDef[] = [
        {
            field: 'brand',
            title: 'Brand',
            renderCell: (row) => {
                const brand = row
                const brandLocal = loadedBrandsDict![brand?.id]
                return (
                    <div
                        className="organizationBrandColumn"
                        style={sty.organizationBrandColumn}
                    >
                        <CustomAvatar
                            className="organizationBrandAvatar1"
                            image={brandLocal?.image}
                            brandOverride={brandLocal}
                            isGradient={false}
                            isBorder
                        />
                        {brandLocal?.name} <BrandTier className="organizationBrandTierTag" tier={brandLocal?.tier} />
                        <AdminId
                            className="organizationBrandAdminId"
                            id={brandLocal?.id}
                            label="brandId"
                        />
                    </div>
                )
            }
        },
        {
            field: 'actions',
            title: '',
            flex: 0,
            width: 212,
            renderCell: (row) => {
                const brand = row
                return (
                    <div className="organizationBrandActions" style={sty.organizationBrandActions}>
                        <ButtonSymphony
                            className="organizationBrandActionsButton"
                            variant="text"
                            onClick={async () => {
                                const brandLocal = await getBrand(brand.id);
                                setCurrentBrand(brandLocal);
                                history.push('/')
                            }}
                        >
                            Go To Dashboard
                        </ButtonSymphony>
                        <div
                            ref={(el) => {
                                if (el) {
                                    menuIconRefs.current.set(brand.id, el);
                                }
                            }}
                            style={sty.menuIconContainer}
                        >
                            <Icon
                                className="contextualMenuButtonIcon"
                                icon="contextualMenu"
                                size={24}
                                color={Colors.greyDark}
                                onClick={() => {
                                    // Set the brand and show the popover
                                    setSelectedBrand(brand)
                                    setActiveBrandId(brand.id)
                                    // Use the specific ref to anchor the popover
                                    setAnchorEl(menuIconRefs.current.get(brand.id) || null)
                                }}
                            />
                        </div>
                        <Popover
                            open={isPopoverOpenForBrand(brand.id)}
                            onClose={closePopover}
                            anchorEl={anchorEl}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        >
                            <div className="brandActionsPopover" style={sty.brandActionsPopover}>
                                <ButtonSymphony
                                    className="brandActionsPopoverButton"
                                    variant="text"
                                    style={sty.brandActionsPopoverButton}
                                    iconLeft="edit"
                                    color="grey"
                                    onClick={() => {
                                        // Open the edit team member modal with the selected brand's team
                                        closePopover()

                                        // If you have team members for this brand, you could select one and open the edit modal
                                        if (teamRowData.length > 0) {
                                            setSelectedBrand(selectedBrand)
                                            setOrganizationUsersTemp(organizationUsers)
                                            setIsEditBrandModalOpen(true)
                                        }
                                    }}
                                >
                                    Edit Team Members
                                </ButtonSymphony>
                                {hasOrganizationAdminAccess() &&
                                    <ButtonSymphony
                                        className="brandActionsPopoverButton"
                                        variant="text"
                                        style={sty.brandActionsPopoverButton}
                                        iconLeft="close"
                                        color="grey"
                                        onClick={() => {
                                            closePopover()
                                            setIsRemoveBrandModalOpen(true)
                                        }}
                                    >
                                        Remove From Team
                                    </ButtonSymphony>
                                }
                            </div>
                        </Popover>
                    </div>
                )
            }
        }
    ]

    const teamRowData = organizationUsers || []

    const teamColumns: ColDef[] = [
        {
            field: 'info',
            title: 'User',
            renderCell: (row) => {
                const user = row
                const {
                    email,
                    name,
                    user_id,
                } = user
                return (
                    <div className="organizationTeamColumn" style={sty.organizationTeamColumn}>
                        <CustomAvatar
                            className="organizationTeamAvatar1"
                            image={getGravatarUrl(email)}
                        />
                        <div className="organizationTeamColumnText">
                            <div style={Sty.text14bold}>
                                {name} {currentUser?.id === user_id && <span style={Sty.text12grey}>(YOU)</span>}
                            </div>
                            <div>{email}</div>
                            <AdminId
                                className="organizationTeamAdminId"
                                id={user_id}
                                label="userId"
                            />
                        </div>
                    </div>
                )
            }
        },
        {
            field: 'role',
            title: 'Role',
            renderCell: (row) => {
                const user = row
                return (
                    <div className="organizationTeamRole">
                        {capitalizeFirstLetter(user?.role)}
                    </div>
                )
            }
        },
        {
            field: 'brands',
            title: 'Brands',
            renderCell: (row) => {
                const user = row
                const isUserInvitationPending = user?.invitation_status === OrganizationInvitationStatus.PENDING
                return (
                    <div
                        className="organizationTeamBrands"
                        style={{
                            display: 'flex',
                            flexDirection: 'row-reverse'
                        }}
                    >
                        {isUserInvitationPending ?
                            <div>Invitation Pending</div>
                            :
                            user?.brands?.map((brand: CurrentBrand, index: number) => {
                                const brandLocal = loadedBrandsDict?.[brand?.id]
                                return (
                                    <CustomAvatar
                                        key={`brand-avatar-${user?.id}-${brand?.id}-${index}`}
                                        className="organizationTeamAvatar2"
                                        style={{ marginRight: -8, border: '2px solid #fff' }}
                                        image={brandLocal?.image}
                                        brandOverride={brandLocal}
                                        size={28}
                                        isGradient={false}
                                    />
                                )
                            })}
                    </div>
                )
            }
        },
        {
            field: 'actions',
            title: '',
            width: 154,
            renderCell: (row) => {
                const user = row
                const userIsMe = user?.user_id === currentUser?.id
                if (userIsMe || !hasOrganizationAdminAccess()) {
                    return (
                        <div className="organizationDividerBlank" style={{ width: 154 }} />
                    )
                }

                return (
                    <ButtonSymphony
                        className="organizationTeamActionsButton"
                        onClick={() => {
                            setSelectedTeamMember(user)
                            setIsEditTeamMemberModalOpen(true)
                        }}
                        variant="text"
                        width={154}
                    >
                        Edit Permissions
                    </ButtonSymphony>
                )
            }
        }
    ]

    const marketingCampaignColumns: ColDef[] = [
        {
            field: 'brand',
            title: 'Brand',
            renderCell: (row) => {
                const campaign = row
                const brandId = campaign?.brandId
                const brandLocal = loadedBrandsDict?.[brandId]
                return (
                    <div className="organizationMarketingCampaignProfile" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <CustomAvatar
                            className="organizationTeamAvatar3"
                            image={brandLocal?.image}
                            brandOverride={brandLocal}
                            isGradient={false}
                        />
                        <div>{brandLocal?.name}</div>
                    </div>
                )
            },
        },
        {
            field: 'status',
            title: 'Status',
            renderCell: (row) => {
                const campaign = row
                return (
                    <TagSymphony
                        className="organizationMarketingCampaignStatusTag"
                        title={CAMPAIGN_STATUS_INFO[campaign?.status as CampaignStatus]?.title}
                        color={CAMPAIGN_STATUS_INFO[campaign?.status as CampaignStatus]?.color}
                        type="square"
                    />
                )
            }
        },
        {
            field: 'campaignName',
            title: 'Name',
            renderCell: (row) => {
                const campaign = row
                const campaignType = campaign?.type as PlaybookType | CampaignType
                return (
                    <div className="organizationMarketingCampaignType">
                        <TagSymphony
                            className="organizationMarketingCampaignTypeTag"
                            title={PLAYBOOK_TYPE_INFO[campaignType]?.title}
                            color={PLAYBOOK_TYPE_INFO[campaignType]?.color}
                            type="square"
                        />
                    </div>
                )
            }
        },
        {
            field: 'budget',
            title: 'Budget',
            renderCell: (row) => {
                const campaign = row
                return (
                    <div className="organizationMarketingCampaignBudget">
                        {campaign.type === CampaignType.PRE_SAVE || campaign.type === PlaybookType.PRESAVE ?
                            '–'
                            :
                            formatNumberToDisplayType(campaign?.budget, 'currency', campaign?.currency)}
                    </div>
                )
            }
        },
        {
            field: 'campaignLength',
            title: 'Length',
            renderCell: (row) => {
                const campaign = row
                const campaignLength = campaign?.endDate ? dayjs(campaign?.endDate).diff(dayjs(campaign?.startDate), 'day') : 0
                return (
                    <div
                        className="organizationMarketingCampaignLength"
                        style={{ display: 'flex', alignItems: 'center', gap: 4, width: '100%' }}
                    >
                        <div>
                            {campaignLength > 0 ?
                                `${campaignLength} ${pluralize('day', campaignLength)}`
                                :
                                '–'
                            }
                        </div>
                        <Icon
                            className="organizationMarketingCampaignLengthIcon"
                            style={{ marginLeft: 'auto' }}
                            icon="edit"
                            onClick={() => {
                                history.push(`/marketing/campaigns/${campaign?.id}`)
                            }}
                        />
                    </div>
                )
            }
        }
    ]

    const websiteColumns: ColDef[] = [
        {
            field: 'brand',
            title: 'Brand',
            flex: 1,
            renderCell: (row) => {
                const website = row
                const {
                    brandId,
                } = website
                const brandLocal = loadedBrandsDict?.[brandId]
                return (
                    <div
                        className="organizationWebsiteProfile"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8
                        }}
                    >
                        <CustomAvatar
                            className="organizationTeamAvatar4"
                            image={brandLocal?.image}
                            brandOverride={brandLocal}
                            isGradient={false}
                        />
                        <div className="organizationWebsiteBrandName">
                            {brandLocal?.name}
                        </div>
                    </div>
                )
            }
        },
        {
            field: 'websiteName',
            title: 'Website Name',
            flex: 1,
            renderCell: (row) => {
                const website = row
                const {
                    name,
                    slug,
                } = website

                return (
                    <div className="organizationWebsiteName">
                        {name || slugToTitle(slug)}
                    </div>
                )
            }
        },
        {
            field: 'created',
            title: 'Created',
            width: 120,
            renderCell: (row) => {
                const website = row
                return (
                    <div className="organizationWebsiteCreated">
                        {dayjs(website?.createdAt).format('MMM D, YYYY')}
                    </div>
                )
            }
        },
        {
            field: 'websiteUrlActions',
            title: 'Website URL & Actions',
            flex: 2,
            renderCell: (row) => {
                const website = row
                return (
                    <div className="organizationWebsiteUrl">
                        {website?.url}
                    </div>
                )
            }
        },
        {
            field: 'actions',
            title: '',
            width: 94,
            renderCell: (row) => {
                const website = row
                const {
                    slug: websiteSlug,
                    brandSlug: brandSlugLocal,
                    brandId,
                    brand
                } = website

                const brandSlug = brandSlugLocal || brand?.name

                return (
                    <div className="organizationWebsiteActions" style={{ display: 'flex', gap: 16 }}>
                        <Icon
                            className="organizationWebsiteActionsButton"
                            icon="edit"
                            size={24}
                            onClick={async () => {
                                const brand = await getBrand(brandId)
                                setCurrentBrand(brand)
                                if(website.websiteContent) {
                                    history.push(`/website-builder/${brandSlug}/${websiteSlug}`)
                                } else {
                                    history.push(`/website/${brandSlug}/${websiteSlug}`)
                                }
                            }}
                        />
                        <Icon
                            className="organizationWebsiteActionsButton"
                            icon="externalLink"
                            size={24}
                            onClick={() => {
                                window.open(website?.url, '_blank')
                            }}
                        />
                    </div>
                )
            }
        }
    ]

    const isUserInvitationPending = (invitationStatus: OrganizationInvitationStatus) => {
        return invitationStatus === OrganizationInvitationStatus.PENDING
    }

    const fetchCampaigns = async (page: number) => {
        if (!organization) {
            return;
        }

        setIsLoadingCampaigns(true);
        const offset = page * PAGE_SIZE;
        const response = await getOrganizationCampaigns_api(organizationId, offset, PAGE_SIZE);
        if (response) {
            setMarketingCampaigns(response.campaigns);
            setTotalCampaigns(response.total);
        }
        setIsLoadingCampaigns(false);
    };

    const fetchWebsites = async () => {
        if (!organization) {
            return;
        }

        try {
            setIsLoadingWebsites(true);
            const res = await getOrganizationWebsites_api(organizationId, websitesPage * PAGE_SIZE, PAGE_SIZE);

            if (res?.websites) {
                const websitesWithFullUrls = res.websites.map((website: any) => {
                    const {
                        slug: websiteSlug,
                        brandSlug: brandSlugLocal,
                        brand
                    } = website
                    const brandSlug = brandSlugLocal || brand?.name
                    const websiteUrl = `${SYMPHONY_WEBSITE_URL}/${brandSlug}/${websiteSlug}`
                    return {
                        ...website,
                        url: websiteUrl
                    }
                });
                setWebsites(websitesWithFullUrls);
            } else {
                throw new Error('Failed to fetch websites');
            }
        } catch (error) {
            // toastError('Failed to load websites');
        } finally {
            setIsLoadingWebsites(false);
        }
    };

    const isTeamAlreadySetup = Boolean(isOrganizationInitialized && organizationId && (invitationStatus === OrganizationInvitationStatus.ACCEPTED || organization?.owner_id === currentUser?.id))

    const getOrganization_local = async () => {
        if (isLoadingOrganization) {
            return;
        }

        await getOrganization({
            organizationId: organizationId,
            brandId: currentBrand?.id,
            userId: currentUser?.id,
        });
    }

    const handleTabChange = async (tab: string | TabItem) => {
        const selectedTab = typeof tab === 'string' ? ORGANIZATION_TABS.find(t => t.id === tab) || ORGANIZATION_TABS[0] : tab

        // Only update URL if it's a user click (string means it's from TabsSymphony click)
        const params = new URLSearchParams(location.search)
        params.set('tab', selectedTab.id)
        history.push({ search: params.toString() })

        setSelectedOrganizationTab(selectedTab)

        switch (selectedTab.id) {
            case OrganizationTab.BRANDS:
                await getBrands();
                break;
            case OrganizationTab.CAMPAIGNS:
                await fetchCampaigns(marketingCampaignsPage);
                break;
            case OrganizationTab.WEBSITES:
                await fetchWebsites();
                break;
            default:
                break;
        }
    }

    useEffect(() => {
        getOrganization_local();

        const params = new URLSearchParams(location.search)
        const tabFromUrl = params.get('tab')
        if (tabFromUrl) {
            const matchingTab = ORGANIZATION_TABS.find(tab => tab.id === tabFromUrl)
            if (matchingTab && matchingTab.id !== selectedOrganizationTab.id) {
                handleTabChange(matchingTab)
            }
        }
    }, [location.search, selectedOrganizationTab])

    useEffect(() => {
        fetchWebsites();
    }, [websitesPage]);

    useEffect(() => {
        fetchCampaigns(marketingCampaignsPage);
    }, [marketingCampaignsPage]);

    const createOrganization_local = async () => {
        let organizationIdLocal = organizationId;

        switch (organizationStatus) {
            case OrganizationStatus.WAITING_FOR_DETAILS:
            case OrganizationStatus.NOT_STARTED:
            case OrganizationStatus.READY:
                // NOTE: do nothing
                await updateOrganization({
                    organizationId: organizationIdLocal,
                    name: organizationNameText,
                    setupStatus: OrganizationStatus.READY,
                })
                break;
            default:
                const res = await createOrganization({
                    name: organizationNameText,
                    emails: [],
                    setupStatus: OrganizationStatus.READY,
                    ownerId: currentUser?.id || 0,
                })
                if (res) {
                    organizationIdLocal = res.organization.id;
                }
                break;
        }

        await Promise.all(
            selectedBrandIdsForOnboarding.map(brandId =>
                createOrganizationBrand({
                    organizationId: organizationIdLocal,
                    brandId,
                })
            )
        );

        setIsCreateTeam(false)

        await getBrands();
    }

    const inviteOrganizationUser = async () => {
        // NOTE: inviting a single user
        await inviteOrganizationUsers({
            organizationId: organizationId,
            users: [{
                firstName: firstNameText,
                lastName: lastNameText,
                email: emailText,
                role: roleNewTeamMemberSelected as OrganizationRole,
            }],
            brands: loadedBrands || [],
        })

        setIsAddTeamMemberModalOpen(false)
    }

    const getPermissionLevelText = (permissionLevel: UserPermissionOnBrand) => {
        switch (permissionLevel) {
            case UserPermissionOnBrand.OWNER:
                return 'Owner'
            default:
                return permissionLevel
        }
    }

    const renderLoadingState = () => {
        return (
            <div className="organizationPageLoaderContainer" style={sty.organizationPageLoaderContainer}>
                <Loader
                    className="organizationPageLoader"
                    title="Loading..."
                    size={32}
                />
            </div>
        )
    }

    const renderOrganizationSetupState = () => {
        const brandsFiltered = loadedBrands?.filter(brand => !organizationBrandIds?.includes(brand.id))
        return (
            <>
                {invitationStatus !== OrganizationInvitationStatus.PENDING &&
                    <InputSymphony
                        className="organizationNameInput"
                        label="What do you want to call your team?"
                        value={organizationNameText}
                        onChange={(value) => setOrganizationNameText(value)}
                        placeholder="Team Name"
                    />
                }
                <CardSymphony
                    className="organizationSetupBrandsCard"
                    title="Select the brands you want to add to your Team"
                    subtitle="You can easily add and upgrade additional profiles after setup."
                    size="small"
                    isNoPadding
                />

                <div className="organizationSetupBrands" style={sty.organizationSetupBrands}>
                    {brandsFiltered?.map((brand, index) => {
                        return (
                            <div
                                key={`brand-${brand.id}-${index}`}
                                className="organizationSetupBrand"
                                style={styx(
                                    sty.organizationSetupBrand,
                                    {
                                        borderBottom: index === brandsFiltered.length - 1 ? 'none' : Sty.border,
                                    }
                                )}
                            >
                                <CustomAvatar
                                    className="organizationBrandAvatar2"
                                    image={brand?.image}
                                    brandOverride={brand}
                                    isGradient={false}
                                />
                                {brand?.name}
                                <BrandTier
                                    className="organizationSetupBrandTierTag"
                                    tier={brand?.tier}
                                />
                                <SwitchSymphony
                                    className="organizationSetupBrandSwitch"
                                    style={{
                                        marginLeft: 'auto',
                                    }}
                                    checked={selectedBrandIdsForOnboarding.includes(brand.id)}
                                    onChange={(value) => {
                                        if (value) {
                                            setSelectedBrandIdsForOnboarding([...selectedBrandIdsForOnboarding, brand.id])
                                        } else {
                                            setSelectedBrandIdsForOnboarding(selectedBrandIdsForOnboarding.filter((id) => id !== brand.id))
                                        }
                                    }}
                                    type={SwitchType.CHECKBOX}
                                />
                            </div>
                        )
                    })}
                </div>
                {invitationStatus === OrganizationInvitationStatus.PENDING ?
                    <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
                        <ButtonSymphony
                            className="organizationNameButton"
                            onClick={async () => {
                                await respondOrganizationUserInvite({
                                    organizationId: organizationId,
                                    userId: currentUser?.id || 0,
                                    invitationStatus: OrganizationInvitationStatus.REJECTED,
                                })

                                history.push('/')
                            }}
                            color="red"
                            variant="outlined"
                        >
                            Reject Invitation
                        </ButtonSymphony>
                        <ButtonSymphony
                            className="organizationNameButton"
                            onClick={async () => {
                                await respondOrganizationUserInvite({
                                    organizationId: organizationId,
                                    userId: currentUser?.id || 0,
                                    invitationStatus: OrganizationInvitationStatus.ACCEPTED,
                                    brandIds: selectedBrandIdsForOnboarding,
                                })
                            }}
                            isLoading={isLoadingOrganizationAction}
                            loadingText="Accepting invitation..."
                        >
                            Accept Invitation
                        </ButtonSymphony>
                    </div>
                    :
                    <ButtonSymphony
                        className="organizationNameButton"
                        style={{
                            marginLeft: 'auto',
                        }}
                        onClick={createOrganization_local}
                        disabled={selectedBrandIdsForOnboarding.length === 0 || organizationNameText.length === 0}
                        isLoading={isLoadingOrganization}
                        loadingText="Creating team..."
                    >
                        Finish Setup
                    </ButtonSymphony>
                }
            </>
        )
    }

    const renderOrganizationTabs = () => {
        return (
            <TabsSymphony
                className="organizationTabs"
                tabs={ORGANIZATION_TABS}
                selectedTab={selectedOrganizationTab}
                onChange={handleTabChange}
                isBorderBottom
                isLoading={isLoadingOrganization || isLoadingCampaigns || isLoadingWebsites}
            />
        )
    }

    const renderOrganizationBrandTab = () => {
        return (
            <TabSymphony
                className="organizationBrandTab"
                visible={selectedOrganizationTab.id === OrganizationTab.BRANDS}
                style={{ gap: 16 }}
            >
                <div className="organizationTableHeader" style={sty.organizationTableHeader}>
                    <div style={Sty.text16bold}></div>
                    <div className="organizationHeaderActions" style={sty.organizationHeaderActions}>
                        <InputSymphony
                            className="organizationBrandSearchInput"
                            placeholder="Search"
                            value={brandSearchText}
                            onChange={(value) => setBrandSearchText(value)}
                            maxWidth={SEARCH_INPUT_MAX_WIDTH}
                            isSearch
                        />
                        <ButtonSymphony
                            className="organizationBrandButton"
                            onClick={() => {
                                setIsAddBrandModalOpen(true)
                            }}
                        >
                            Add Brand
                        </ButtonSymphony>
                    </div>
                </div>
                <TableElement
                    className="organizationBrandTable"
                    columnDefs={brandColumns}
                    rowData={brandRowData.filter((brand) => brand?.name?.toLowerCase().includes(brandSearchText.toLowerCase()))}
                    emptyTableMessage="No brands added"
                    paginationPageSize={25}
                />
            </TabSymphony>
        )
    }

    const renderOrganizationTeamTab = () => {
        return (
            <TabSymphony
                className="organizationTeamTab"
                visible={selectedOrganizationTab.id === OrganizationTab.TEAM}
                style={{ gap: 16 }}
            >
                <div className="organizationTableHeader" style={sty.organizationTableHeader}>
                    <div style={Sty.text16bold}></div>
                    <div className="organizationHeaderActions" style={sty.organizationHeaderActions}>
                        <InputSymphony
                            className="organizationTeamSearchInput"
                            placeholder="Search"
                            value={teamMemberSearchText}
                            onChange={(value) => setTeamMemberSearchText(value)}
                            maxWidth={SEARCH_INPUT_MAX_WIDTH}
                            isSearch
                        />
                        {hasOrganizationAdminAccess() &&
                            <ButtonSymphony
                                className="organizationTeamButton"
                                onClick={() => {
                                    setRoleNewTeamMemberSelected(OrganizationRole.MEMBER)
                                    setIsAddTeamMemberModalOpen(true)
                                }}
                            >
                                Add Team Member
                            </ButtonSymphony>
                        }
                    </div>
                </div>
                <TableElement
                    className="organizationTeamTable"
                    columnDefs={teamColumns}
                    rowData={[...teamRowData]
                        .sort((a, b) => {
                            // Current user always appears at the top
                            if (a?.user_id === currentUser?.id) return -1;
                            if (b?.user_id === currentUser?.id) return 1;

                            // Sort by role - administrators first, then members
                            if (a?.role !== b?.role) {
                                return a?.role === OrganizationRole.ADMINISTRATOR ? -1 : 1;
                            }

                            // Then sort alphabetically by name
                            return a?.name?.localeCompare(b?.name)
                        })
                        .filter((user) => {
                            const userName = user?.name || '';
                            const userEmail = user?.email || '';
                            return userName.toLowerCase().includes(teamMemberSearchText.toLowerCase()) ||
                                userEmail.toLowerCase().includes(teamMemberSearchText.toLowerCase());
                        })}
                    emptyTableMessage="No team members"
                    paginationPageSize={25}
                />
            </TabSymphony>
        )
    }

    const renderOrganizationMarketingCampaignsTab = () => {
        return (
            <TabSymphony
                className="organizationMarketingCampaignsTab"
                visible={selectedOrganizationTab.id === OrganizationTab.CAMPAIGNS}
                style={{ gap: 16 }}
            >
                <div className="organizationTableHeader" style={sty.organizationTableHeader}>
                    <div style={Sty.text16bold}></div>
                    <div className="organizationHeaderActions" style={sty.organizationHeaderActions}>
                        <ButtonSymphony
                            className="organizationBrandButton"
                            onClick={() => {
                                setCreateGoal(TeamPlanCreateGoal.CAMPAIGN);
                                setIsBrandSelectorModalOpen(true);
                            }}
                        >
                            Create a Campaign
                        </ButtonSymphony>
                    </div>
                </div>
                {isLoadingCampaigns && marketingCampaigns.length === 0 ?
                    renderLoadingState()
                    :
                    <TableElement
                        className="organizationMarketingCampaignsTable"
                        columnDefs={marketingCampaignColumns}
                        rowData={marketingCampaigns}
                        emptyTableMessage="No marketing campaigns"
                        paginationPageSize={25}
                    />
                }
            </TabSymphony>
        )
    }

    const renderOrganizationWebsitesTab = () => {
        return (
            <TabSymphony
                className="organizationWebsitesTab"
                visible={selectedOrganizationTab.id === OrganizationTab.WEBSITES}
                style={{ gap: 16 }}
            >
                <div className="organizationTableHeader" style={sty.organizationTableHeader}>
                    <div style={Sty.text16bold}></div>
                    <div className="organizationHeaderActions" style={sty.organizationHeaderActions}>
                        <ButtonSymphony
                            className="organizationBrandButton"
                            onClick={() => {
                                setCreateGoal(TeamPlanCreateGoal.WEBSITE);
                                setIsBrandSelectorModalOpen(true);
                            }}
                        >
                            Create a Website
                        </ButtonSymphony>
                    </div>
                </div>
                {isLoadingWebsites && websites.length === 0 ?
                    renderLoadingState()
                    :
                    <TableElement
                        className="organizationWebsitesTable"
                        columnDefs={websiteColumns}
                        rowData={websites}
                        emptyTableMessage="No websites"
                        paginationPageSize={25}
                    />
                }
            </TabSymphony>
        )
    }

    const renderOrganizationFansTab = () => {
        return (
            <TabSymphony
                className="organizationFansTab"
                visible={selectedOrganizationTab.id === OrganizationTab.FANS}
            >
                <h1>Fans</h1>
            </TabSymphony>
        )
    }

    const renderOrganizationBillingTab = () => {
        if (selectedOrganizationTab.id !== OrganizationTab.BILLING) {
            return null;
        }

        return (
            <TabSymphony
                className="organizationBillingTab"
                visible={true}
            >
                <OrganizationTabBilling className="organizationBillingTabFromOrganizationPageV2" />
            </TabSymphony>
        )
    }

    const renderOrganizationPage = () => {
        if (!isSymphonyAdmin && (!isTeamAlreadySetup || isCreateTeam)) {
            return (
                <CardSymphony
                    className="organizationNeedsSetupCard"
                    title="Team Setup"
                    subtitle="You can manage unlimited profiles through your Team."
                >
                    {renderOrganizationSetupState()}
                </CardSymphony>
            )
        }

        return (
            <CardSymphony
                className="organizationPageCard"
                title={organization?.name || 'Organization Page V2'}
                subtitle={
                    <div className="organizationPageSubtitle" style={sty.organizationPageSubtitle}>
                        <AdminId
                            className="organizationPageAdminId"
                            id={organization?.id}
                            label="organizationId"
                        />
                        <div>Team Members <span style={Sty.text18}>{organizationUsers?.length}</span></div>
                        <div>Brands <span style={Sty.text18}>{organizationBrandIds?.length}</span></div>
                    </div>
                }
            >
                {renderOrganizationTabs()}

                {renderOrganizationBrandTab()}
                {renderOrganizationTeamTab()}
                {renderOrganizationMarketingCampaignsTab()}
                {renderOrganizationWebsitesTab()}
                {renderOrganizationFansTab()}
                {renderOrganizationBillingTab()}
            </CardSymphony>
        )
    }

    // Helper function to check if a specific brand's popover is open
    const isPopoverOpenForBrand = (brandId: string | number) => {
        return activeBrandId === brandId
    }

    // Function to close any open popover
    const closePopover = () => {
        setActiveBrandId(null)
        setAnchorEl(null)
    }

    // NOTE: render()
    return (
        <>
            <style>
                {`
                    .brandSelectorItem:hover {
                        background-color: ${Colors.greyLight};
                    }
                    .brandActionsPopoverButton:hover {
                        background-color: ${Colors.greyLight} !important;
                    }
                `}
            </style>
            <PageSymphony
                className="organizationPageComponent"
                isContent
            >
                <DebugBox
                    // alwaysShow
                    data={{
                        websites,
                        // marketingCampaigns,
                        // organization_OLD,
                        // organization,
                        // organizationUsers,
                        // organizationBrandIds,
                        // loadedBrandsDict,
                        // selectedBrand,
                        // teamRowData,
                        // teamColumns,
                        // organizationId: organization?.id,
                        // isTeamAlreadySetup,
                        // isOrganizationInitialized,
                        // invitationStatus,
                        // marketingCampaigns,
                        // websites,
                        // currentBrand,
                        // organization,
                        // organizationUsers,
                        // isLoadingOrganization,
                        // isOrganizationInitialized,
                    }}
                />
                {organization || isCreateTeam ?
                    renderOrganizationPage()
                    :
                    <CardSymphony
                        className="organizationPageCard"
                        title="Members"
                        subtitle="Invite other users to your brand."
                    >
                        <MembersPage
                            className="organizationPageComponent"
                            brandBelongsToOrganization={false}
                            onClickCreateTeam={() => {
                                setIsCreateTeam(true)
                            }}
                        />
                    </CardSymphony>
                }
            </PageSymphony>

            {/* MODALS */}
            <ModalSymphony
                className="organizationAddTeamMemberModal"
                style={{ gap: 8 }}
                isOpen={isAddTeamMemberModalOpen}
                onClose={() => setIsAddTeamMemberModalOpen(false)}
                title="Add a new team member"
                subtitle="Invite a new team member to your organization"

                buttonRightText="Send Invite"
                onClickButtonRight={inviteOrganizationUser}
                buttonRightIsLoading={isLoadingOrganizationAction}
                buttonRightLoadingText="Sending..."
            >
                <div style={{ display: 'flex', gap: 16 }}>
                    <InputSymphony
                        className="organizationAddTeamMemberInput1"
                        placeholder="First Name"
                        label="First Name (Optional)"
                        value={firstNameText}
                        onChange={(value) => setFirstNameText(value)}
                    />
                    <InputSymphony
                        className="organizationAddTeamMemberInput2"
                        placeholder="Last Name"
                        label="Last Name (Optional)"
                        value={lastNameText}
                        onChange={(value) => setLastNameText(value)}
                    />
                </div>
                <InputSymphony
                    className="organizationAddTeamMemberInput3"
                    placeholder="Email"
                    label="Email"
                    value={emailText}
                    onChange={(value) => setEmailText(value)}
                />
                <InputSymphony
                    className="organizationAddTeamMemberInput4"
                    label="Role"
                    value={roleNewTeamMemberSelected}
                    isSelectDropdown
                    disabled={!hasOrganizationAdminAccess()}
                    onOptionSelect={(option) => setRoleNewTeamMemberSelected(option.value)}
                    options={[
                        {
                            title: 'Admin',
                            value: OrganizationRole.ADMINISTRATOR,
                            description: 'Team Admins have full access to add and edit profiles, invite new teammates, create campaigns and websites, and update Billing settings.'
                        },
                        {
                            title: 'Member',
                            value: OrganizationRole.MEMBER,
                            description: 'This teammate will have only have access to selected profiles, with customizable Admin or View-only permissions per profile.'
                        }
                    ]}
                />
            </ModalSymphony>

            <ModalSymphony
                className="organizationEditTeamMemberModal"
                style={{ gap: 16 }}
                isOpen={isEditTeamMemberModalOpen}
                onClose={() => setIsEditTeamMemberModalOpen(false)}
                title={selectedTeamMember?.email}
                subtitle="Edit permissions and access to brands"

                buttonRightText="Save Changes"
                onClickButtonRight={async () => {
                    await updateOrganizationUser({
                        organizationId: organizationId,
                        userId: selectedTeamMember?.user_id!,
                        role: selectedTeamMember?.role as OrganizationRole,
                        brands: selectedTeamMember?.brands || [],
                    })

                    setIsEditTeamMemberModalOpen(false)
                }}
                buttonRightIsLoading={isLoadingOrganizationAction}
                buttonRightLoadingText="Saving..."

                buttonLeftText="Remove Member"
                onClickButtonLeft={() => {
                    setIsRemoveTeamMemberModalOpen(true)
                }}
                buttonLeftColor="red"
                buttonLeftVariant="outlined"
            >
                <InputSymphony
                    className="organizationEditTeamMemberInput"
                    label="User's Team Permission Level"
                    value={getPermissionLevelText(selectedTeamMember?.role)}
                    isSelectDropdown
                    onOptionSelect={(option) => {
                        if (selectedTeamMember) {
                            setSelectedTeamMember({
                                ...selectedTeamMember,
                                role: option.value as OrganizationRole
                            });
                        }
                    }}
                    options={[
                        ...(isSymphonyAdmin ? [{
                            title: 'Owner',
                            value: OrganizationRole.OWNER,
                            description: 'The owner of the organization has full access to all profiles, brands, and settings.'
                        }] : []),
                        {
                            title: 'Admin',
                            value: OrganizationRole.ADMINISTRATOR,
                            description: 'Team Admins have full access to add and edit profiles, invite new teammates, create campaigns and websites, and update Billing settings.'
                        },
                        {
                            title: 'Member',
                            value: OrganizationRole.MEMBER,
                            description: 'This teammate will have only have access to selected profiles, with customizable Admin or View-only permissions per profile.'
                        }
                    ]}
                />
                {!isUserInvitationPending(selectedTeamMember?.invitation_status) &&
                    <>
                        <div className="organizationEditTeamMemberAccess" style={sty.organizationEditTeamMemberAccess}>
                            This teammate has access to:
                        </div>

                        <div className="organizationEditTeamMemberBrands" style={sty.organizationEditTeamMemberBrands}>
                            {selectedTeamMember?.brands.map((brand: CurrentBrand, index: number) => {
                                const brandLocal = loadedBrandsDict?.[brand?.id]
                                const {
                                    name,
                                    image,
                                } = brandLocal || {} // NOTE: use loadedBrandsDict to get the image

                                const {
                                    permissionLevel,
                                } = brand // NOTE: use selectedTeamMember.brands to get the current permissionLevel

                                return (
                                    <div
                                        key={`brand-${selectedTeamMember?.user_id}-${brand?.id}-${index}`}
                                        className="organizationEditTeamMemberBrand"
                                        style={sty.organizationEditTeamMemberBrand}
                                    >
                                        <CustomAvatar
                                            className="organizationEditTeamMemberBrandAvatar"
                                            image={image}
                                            brandOverride={brandLocal}
                                        />
                                        <div>
                                            {name}
                                        </div>
                                        <InputSymphony
                                            className="organizationEditTeamMemberBrandInput"
                                            style={{ width: '100%', flexShrink: 0, marginLeft: 'auto' }}
                                            value={getPermissionLevelText(permissionLevel)}
                                            disabled={permissionLevel === UserPermissionOnBrand.OWNER}
                                            isSelectDropdown
                                            maxWidth={200}
                                            onOptionSelect={(option) => {
                                                if (selectedTeamMember) {
                                                    setSelectedTeamMember({
                                                        ...selectedTeamMember,
                                                        brands: selectedTeamMember.brands.map((b: CurrentBrand) => b.id === brand.id ? { ...b, permissionLevel: option.value as UserPermissionOnBrand } : b)
                                                    });
                                                }
                                            }}
                                            options={[
                                                {
                                                    title: 'Full Access',
                                                    value: UserPermissionOnBrand.ADMINISTRATOR,
                                                    description: 'Allows this teammate to create and edit campaigns & websites.'
                                                },
                                                {
                                                    title: 'View Only',
                                                    value: UserPermissionOnBrand.VIEW_ONLY,
                                                    description: 'Allows this teammate to view campaigns & websites, but not edit or create them.'
                                                }
                                            ]}
                                        />
                                        {/* <Icon
                                            className="organizationEditTeamMemberBrandTrash"
                                            icon="trash"
                                            size={24}
                                            color={Colors.greyDark}
                                            onClick={() => {
                                                if (selectedTeamMember) {
                                                    // Filter out the brand being removed from the team member's brands array
                                                    setSelectedTeamMember({
                                                        ...selectedTeamMember,
                                                        brands: selectedTeamMember.brands.filter((b: CurrentBrand) => b.id !== brand.id)
                                                    });
                                                }
                                            }}
                                        /> */}
                                    </div>
                                )
                            })}
                        </div>
                    </>
                }
            </ModalSymphony>

            <ModalSymphony
                className="organizationEditBrandModal"
                style={{ gap: 16 }}
                isOpen={isEditBrandModalOpen}
                onClose={() => setIsEditBrandModalOpen(false)}
                title={selectedBrand?.name}
                subtitle="Edit team members and permissions on this brand"

                buttonRightText="Save Changes"
                buttonRightIsLoading={isLoadingOrganizationAction}
                buttonRightLoadingText="Saving..."
                onClickButtonRight={async () => {
                    await updateUserBrand({
                        organizationId: organizationId,
                        brandId: selectedBrand?.id!,
                        users: organizationUsersTemp?.map((user: any) => ({
                            userId: user.user_id,
                            permissionLevel: user.brands.find((b: CurrentBrand) => b.id === selectedBrand?.id)?.permissionLevel as UserPermissionOnBrand,
                        }))
                    })

                    setIsEditBrandModalOpen(false)
                }}

                buttonLeftText="Remove Brand"
                onClickButtonLeft={() => {
                    setIsRemoveBrandModalOpen(true)
                }}
                buttonLeftColor="red"
                buttonLeftVariant="outlined"
                buttonLeftDisabled={!(hasOrganizationAdminAccess() || selectedBrand?.permissionLevel === UserPermissionOnBrand.OWNER)}
            >
                <div className="organizationEditTeamMemberAccess" style={sty.organizationEditTeamMemberAccess}>
                    <div>
                        This brand has the following team members:
                    </div>
                    <ButtonSymphony
                        className="organizationEditBrandButton"
                        variant="text"
                        iconLeft="addCircle"
                        onClick={() => {
                            setIsAddTeamMemberModalOpen(true)
                        }}
                    >
                        Add a Team Member
                    </ButtonSymphony>
                </div>

                <div className="organizationEditTeamMemberBrands" style={sty.organizationEditTeamMemberBrands}>
                    {organizationUsersTemp?.filter((teamMember: any) => !isUserInvitationPending(teamMember.invitation_status)).map((teamMember: any, index: number) => {
                        const teamMemberBrand = teamMember.brands.find((b: CurrentBrand) => b.id === selectedBrand?.id)
                        const { name, email, user_id } = teamMember
                        return (
                            <div
                                key={`brand-${user_id}-${index}`}
                                className="organizationEditTeamMemberBrand"
                                style={sty.organizationEditTeamMemberBrand}
                            >
                                <CustomAvatar
                                    className="organizationEditTeamMemberBrandAvatar"
                                    image={getGravatarUrl(email)}
                                />
                                <div>
                                    {name || email}
                                </div>
                                <div
                                    className="brandInputContainer"
                                    style={{
                                        display: 'flex',
                                        marginLeft: 'auto',
                                        alignItems: 'center',
                                        gap: 8,
                                        width: 200
                                    }}
                                >
                                    <InputSymphony
                                        className="organizationTeamMemberPermissionSelect"
                                        style={{ width: '100%', flexShrink: 0 }}
                                        value={getPermissionLevelText(teamMemberBrand?.permissionLevel)}
                                        isSelectDropdown
                                        maxWidth={200}
                                        disabled={!hasOrganizationAdminAccess() || teamMemberBrand?.permissionLevel === UserPermissionOnBrand.OWNER}
                                        onOptionSelect={(option) => {
                                            if (teamMember) {
                                                setOrganizationUsersTemp(
                                                    organizationUsersTemp?.map((user: any) =>
                                                        user.user_id === teamMember.user_id
                                                            ? {
                                                                ...user,
                                                                brands: user.brands.map((b: CurrentBrand) =>
                                                                    b.id === selectedBrand?.id
                                                                        ? { ...b, permissionLevel: option.value as UserPermissionOnBrand }
                                                                        : b
                                                                )
                                                            }
                                                            : user
                                                    )
                                                )
                                            }
                                        }}
                                        options={[
                                            {
                                                title: 'Full Access',
                                                value: UserPermissionOnBrand.ADMINISTRATOR,
                                                description: 'Full Access permissions allow this teammate to create and edit campaigns & websites.'
                                            },
                                            {
                                                title: 'View Only',
                                                value: UserPermissionOnBrand.VIEW_ONLY,
                                                description: 'View-only access to the profile.This teammate will not have the ability to edit or create campaigns or websites.'
                                            }
                                        ]}
                                    />
                                </div>
                            </div>
                        )
                    })}
                </div>
            </ModalSymphony>

            <AddBrandToOrganizationModal
                isOpen={isAddBrandModalOpen}
                onClose={() => setIsAddBrandModalOpen(false)}
            />

            <ModalSymphony
                className="organizationRemoveBrandModal"
                isOpen={isRemoveBrandModalOpen}
                onClose={() => setIsRemoveBrandModalOpen(false)}
                title="Remove Brand from Team"
                subtitle={`Are you sure you want to remove "${selectedBrand?.name}" from your team? You won't be able to access their dashboard or campaigns.`}
                onClickButtonRight={async () => {
                    await deleteOrganizationBrand({
                        organizationId: organizationId,
                        brandId: selectedBrand?.id!,
                    })

                    setIsRemoveBrandModalOpen(false)
                }}
                buttonRightText="Confirm Remove Brand"
                buttonRightIsLoading={isLoadingOrganizationAction}
                buttonRightLoadingText="Removing Brand..."
                buttonRightColor="red"
                buttonRightVariant="outlined"

                buttonLeftText="Cancel"
                onClickButtonLeft={() => {
                    setIsRemoveBrandModalOpen(false)
                }}
            />

            <ModalSymphony
                className="organizationRemoveTeamMemberModal"
                isOpen={isRemoveTeamMemberModalOpen}
                onClose={() => setIsRemoveTeamMemberModalOpen(false)}
                title="Remove Team Member"
                subtitle={`Are you sure you want to remove "${selectedTeamMember?.email}" from your team? You won't be able to access their dashboard or campaigns.`}
                onClickButtonRight={async () => {
                    await deleteOrganizationUser({
                        organizationId: organizationId,
                        userId: selectedTeamMember?.user_id!,
                    })

                    setIsRemoveTeamMemberModalOpen(false)
                    setIsEditTeamMemberModalOpen(false)
                }}
                buttonRightText="Confirm Remove Member"
                buttonRightIsLoading={isLoadingOrganizationAction}
                buttonRightLoadingText="Removing Member..."
                buttonRightColor="red"
                buttonRightVariant="outlined"
                buttonLeftText="Cancel"
                onClickButtonLeft={() => {
                    setIsRemoveTeamMemberModalOpen(false)
                }}
            />

            <ModalSymphony
                className="brandSelectorModal"
                isOpen={isBrandSelectorModalOpen}
                onClose={() => setIsBrandSelectorModalOpen(false)}
                title="Select a Profile"
                subtitle={`Choose the profile you want to create a ${createGoal} for.`}
                maxWidth={400}
            >
                <InputSymphony
                    className="brandSelectorSearchInput"
                    placeholder="Search..."
                    value={brandSelectorSearchText}
                    onChange={(value) => setBrandSelectorSearchText(value)}
                    isSearch
                />
                <div
                    className="brandSelectorScrollView"
                    style={styx(
                        sty.brandSelectorScrollView,
                        { boxShadow: Sty.boxShadowInset }
                    )}
                >
                    {organizationBrandIds
                        ?.filter(brandId => {
                            const brand = loadedBrandsDict?.[brandId];
                            return brand?.name?.toLowerCase().includes(brandSelectorSearchText.toLowerCase());
                        })
                        .map((brandId, index) => {
                            const brand = loadedBrandsDict?.[brandId];
                            if (!brand) {
                                return null;
                            }

                            const {
                                tier,
                                name,
                                image,
                            } = brand

                            return (
                                <div
                                    key={`brand-selector-${brandId}-${index}`}
                                    className="brandSelectorItem"
                                    style={sty.brandSelectorItem}
                                    onClick={async () => {
                                        const brand = await getBrand(brandId);
                                        setCurrentBrand(brand);

                                        switch (createGoal) {
                                            case TeamPlanCreateGoal.CAMPAIGN:
                                                setShowCreateNewCampaignModal(true);
                                                break;
                                            case TeamPlanCreateGoal.WEBSITE:
                                                setShowCreateNewWebsiteModalV2(true);
                                                break;
                                        }

                                        setIsBrandSelectorModalOpen(false);
                                    }}
                                >
                                    <CustomAvatar
                                        className="brandSelectorAvatar"
                                        image={image}
                                        brandOverride={brand}
                                    />
                                    <div>{name}</div>
                                    <BrandTier
                                        className="brandSelectorTierTag"
                                        style={{ marginLeft: 'auto' }}
                                        tier={tier}
                                    />
                                </div>
                            );
                        })}
                </div>
            </ModalSymphony>
        </>
    )
}

const sty: Record<string, CSSProperties> = {
    organizationBrandColumn: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 16,
    },
    organizationTeamColumn: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    organizationEditTeamMemberAccess: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
    },
    organizationEditTeamMemberBrand: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        borderBottom: Sty.border,
        paddingBottom: 8,
        width: '100%',
    },
    organizationEditTeamMemberBrands: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    },
    organizationTableHeader: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
    },
    organizationHeaderActions: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    brandActionsPopover: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: 180,
    },
    brandActionsPopoverButton: {
        width: '100%',
        justifyContent: 'flex-start',
        textAlign: 'left',
        borderRadius: 0,
        padding: 8,
    },
    organizationBrandActions: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 32,
    },
    menuIconContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    organizationPageSubtitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 24,
    },
    organizationSetupBrands: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        borderRadius: 16,
        border: Sty.border,
        width: '100%',
        maxWidth: 400,
        marginLeft: 'auto',
        marginRight: 'auto',
    },
    organizationSetupBrand: {
        display: 'flex',
        width: '100%',
        alignItems: 'center',
        gap: 8,
        paddingTop: 8,
        paddingBottom: 8,
        paddingLeft: 16,
        paddingRight: 16,
    },
    brandSelectorScrollView: {
        maxHeight: 300,
        overflowY: 'auto',
        marginTop: 16,
        borderRadius: 8,
        border: Sty.border,
    },
    brandSelectorItem: {
        display: 'flex',
        cursor: 'pointer',
        alignItems: 'center',
        gap: 12,
        padding: 16,
        borderBottom: Sty.border,
    },
    organizationPageLoaderContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: 250,
        borderRadius: 16,
        backgroundColor: Colors.greyLight,
    }
}

export default OrganizationPageV2;